import { useCallback, useEffect, useMemo, useState } from 'react'

import { useAuth } from './use-auth'
import { useQuickPassProfile } from './use-quickpass-profile'
import { useResidentTenancy } from './use-resident-tenancy'
import {
  ExperienceContext,
  ExperienceContextValue,
  ExperienceMode,
} from './use-experience'

const EXPERIENCE_MODE_STORAGE_PREFIX = 'quickpass:experience-mode:'

interface ExperienceProviderProps {
  children: React.ReactNode
}

/**
 * Reads a renter's last explicit land choice. Keyed per user so one account's
 * choice never leaks into another's on a shared device.
 *
 * @param {string} userId - the signed-in renter's QuickPass id
 * @returns {ExperienceMode | null}
 */
const readStoredMode = (userId: string): ExperienceMode | null => {
  if (userId === '') {
    return null
  }
  try {
    const stored = window.localStorage.getItem(
      `${EXPERIENCE_MODE_STORAGE_PREFIX}${userId}`,
    )
    if (stored === 'search' || stored === 'resident') {
      return stored
    }
    return null
  } catch (error) {
    console.warn('[experience] could not read stored mode —', error)
    return null
  }
}

/**
 * Persists a renter's explicit land choice (per user) so it survives reloads.
 *
 * @param {string} userId - the signed-in renter's QuickPass id
 * @param {ExperienceMode} mode - the chosen mode
 * @returns {void}
 */
const persistMode = (userId: string, mode: ExperienceMode): void => {
  if (userId === '') {
    return
  }
  try {
    window.localStorage.setItem(
      `${EXPERIENCE_MODE_STORAGE_PREFIX}${userId}`,
      mode,
    )
  } catch (error) {
    console.warn('[experience] could not persist mode —', error)
  }
}

/**
 * Drives the RBC-style "two lands, one identity" experience. A renter can be an
 * apartment searcher, a resident (has a tenancy), or both. This provider tracks
 * which land is active — honouring an explicit per-user choice, otherwise
 * defaulting to resident when they have a tenancy — and clamps the mode to a
 * land the renter actually qualifies for. `isResolved` stays false until both
 * the profile and tenancy signals have settled (re-arming on a user switch), so
 * consumers like AuthGate never act on stale state. `canSwitch` lets the header
 * show a switcher only when there are genuinely two lands to move between.
 *
 * @param {ExperienceProviderProps} props
 * @returns {JSX.Element}
 */
const ExperienceProvider = ({
  children,
}: ExperienceProviderProps): JSX.Element => {
  const { quickPassUser } = useAuth()
  const { isProfileLoading, hasFinishedOnboarding } = useQuickPassProfile()
  const { isLoadingTenancies, hasResidentAccess } = useResidentTenancy()

  const userId = quickPassUser?.id ?? ''
  const [mode, setModeState] = useState<ExperienceMode>('search')
  const [isResolved, setIsResolved] = useState<boolean>(false)

  const canSearch = hasFinishedOnboarding
  const canResident = hasResidentAccess
  const canSwitch = canSearch === true && canResident === true

  useEffect(() => {
    if (isProfileLoading === true || isLoadingTenancies === true) {
      setIsResolved(false)
      return
    }

    setModeState(() => {
      const stored = readStoredMode(userId)
      if (stored === 'resident' && canResident === true) {
        return 'resident'
      }
      if (stored === 'search' && canSearch === true) {
        return 'search'
      }
      if (canResident === true) {
        return 'resident'
      }
      return 'search'
    })
    setIsResolved(true)
  }, [isProfileLoading, isLoadingTenancies, canResident, canSearch, userId])

  const setMode = useCallback(
    (nextMode: ExperienceMode): void => {
      setModeState(nextMode)
      persistMode(userId, nextMode)
    },
    [userId],
  )

  const value = useMemo<ExperienceContextValue>(
    () => ({
      mode,
      isResolved,
      canSearch,
      canResident,
      canSwitch,
      setMode,
    }),
    [mode, isResolved, canSearch, canResident, canSwitch, setMode],
  )

  return (
    <ExperienceContext.Provider value={value}>
      {children}
    </ExperienceContext.Provider>
  )
}

export { ExperienceProvider }
